import React, { FC, useState, useEffect } from 'react'
// @ts-ignore
import Repeatable from 'react-repeatable'
import { Box } from '@mui/material'
import InputBase from '@mui/material/InputBase'
import { CustomIconFromValue } from './CustomIconFromValue'
import { themeColors } from '@app/constants'
import theme from '@app/lib/materialui/theme'

export interface NumInputProps {
	disabled?: boolean
	max?: number
	min?: number
	startNum?: number
	unit?: string
	size?: 'small' | 'medium' | 'large'
	value?: number
	valueWidth?: number
	onChange: (value: number) => void
}

const NumInput: FC<NumInputProps> = ({
	disabled,
	max,
	min,
	onChange,
	size = 'medium',
	unit = '',
	value,
	valueWidth = 22,
}) => {
	const constrainNumber = (valueToConstrain?: number) => {
		if (
			typeof valueToConstrain !== 'number' ||
			!Number.isFinite(valueToConstrain)
		) {
			return valueToConstrain
		}

		let next = valueToConstrain

		if (typeof min === 'number') {
			next = Math.max(min, next)
		}

		if (typeof max === 'number') {
			next = Math.min(max, next)
		}

		return next
	}

	const constrainedValue = constrainNumber(value)

	const [current, setCurrent] = useState(constrainedValue)

	useEffect(() => {
		setCurrent(constrainedValue)
	}, [constrainedValue])

	const onValueChange = (newValue: number, checkRanges = true) => {
		if (checkRanges && Number.isFinite(newValue)) {
			if (!isNaN(min!) && newValue < min!) {
				newValue = min!
			}

			if (!isNaN(max!) && newValue > max!) {
				newValue = max!
			}
		}

		setCurrent(newValue)
		onChange(newValue)
	}

	const numericCurrent =
		typeof current === 'number' && Number.isFinite(current) ? current : 0

	const incrementValue = () => {
		onValueChange(numericCurrent + 1)
	}

	const decrementValue = () => {
		onValueChange(numericCurrent - 1)
	}

	return (
		<Box
			sx={{
				background: 'white',
				borderRadius: '3px',
				border: 'solid 1px #dddddd',
				//padding: '7px 17px',
				display: 'flex',
				justifyContent: 'space-between',
			}}
		>
			<Box
				sx={{
					alignItems: 'center',
					display: 'flex',
					flexGrow: 1,
					paddingLeft: '10px',
				}}
			>
				<InputBase
					sx={{
						...(size === 'large' && {
							height: 50,
							paddingBottom: theme.spacing(1.1),
							paddingTop: theme.spacing(1.1),
						}),
						...(size === 'small' && {
							fontSize: theme.typography.subtitle2.fontSize,
							paddingBottom: 0,
							paddingTop: 0,
							height: '36px',
						}),
					}}
					disabled={disabled}
					value={Number.isFinite(current) ? current : ''}
					style={{
						width: Number.isFinite(current) ? valueWidth : '100%',
					}}
					onChange={(e) =>
						onValueChange(parseInt(e.target.value), false)
					}
					onBlur={(e) => onValueChange(parseInt(e.target.value))}
				/>
				{unit && Number.isFinite(current) ? (
					<InputBase
						readOnly={true}
						disabled={disabled}
						sx={{
							...(size === 'large' && {
								height: 50,
								paddingBottom: theme.spacing(1.1),
								paddingTop: theme.spacing(1.1),
							}),
							...(size === 'small' && {
								fontSize: theme.typography.subtitle2.fontSize,
								paddingBottom: 0,
								paddingTop: 0,
							}),
						}}
						defaultValue={unit}
						style={{ width: valueWidth }}
					/>
				) : null}
			</Box>
			<Box
				sx={{
					borderRadius: '0 3px 3px 0',
					borderLeft: `solid 1px ${themeColors.borderColor}`,
					display: 'flex',
					flexDirection: 'column',
					'& > div': {
						display: 'flex',
						flexGrow: 1,
					},
				}}
			>
				<Repeatable
					repeatDelay={500}
					repeatInterval={32}
					onHold={incrementValue}
					onRelease={incrementValue}
				>
					<Box
						sx={{
							alignItems: 'center',
							cursor: 'pointer',
							color: '#4a4a4a',
							display: 'flex',
							fontSize: '8px',
							flexGrow: 1,
							...(disabled && {
								color: theme.palette.text.disabled,
								cursor: 'default',
							}),
							...(size === 'large' && {
								paddingLeft: theme.spacing(0.55),
								paddingRight: theme.spacing(0.55),
							}),
							...(size === 'small' && {
								paddingLeft: theme.spacing(0.45),
								paddingRight: theme.spacing(0.45),
							}),
						}}
						style={{
							color: '#bababa',
						}}
					>
						<CustomIconFromValue icon="svg plus" style={{}} />
					</Box>
				</Repeatable>
				<Repeatable
					repeatDelay={500}
					repeatInterval={32}
					onHold={decrementValue}
					onRelease={decrementValue}
				>
					<Box
						sx={{
							alignItems: 'center',
							cursor: 'pointer',
							color: '#4a4a4a',
							display: 'flex',
							fontSize: '8px',
							flexGrow: 1,
							...(disabled && {
								color: theme.palette.text.disabled,
								cursor: 'default',
							}),
							...(size === 'large' && {
								paddingLeft: theme.spacing(0.55),
								paddingRight: theme.spacing(0.55),
							}),
							...(size === 'small' && {
								paddingLeft: theme.spacing(0.45),
								paddingRight: theme.spacing(0.45),
							}),
						}}
						style={{
							borderTop: 'solid 1px #dddddd',
							color: '#bababa',
						}}
					>
						<CustomIconFromValue
							icon="svg minus"
							style={{ marginBottom: 10 }}
						/>
					</Box>
				</Repeatable>
			</Box>
		</Box>
	)
}

export default NumInput
